import { Seo } from "@/components/seo/Seo";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { ArrowLeft, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";

function Section({
  title,
  children,
}: {
  title: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold">{title}</h2>
      <div className="space-y-2 text-sm leading-relaxed text-muted-foreground">
        {children}
      </div>
    </section>
  );
}

export function TermsPage() {
  const lastUpdated = "2026-01-27";

  const navigate = useNavigate();
  const onBack = () => {
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/", { replace: true });
    }
  };

  return (
    <>
      <Seo
        title={t`Terms of Service`}
        description={t`The terms and conditions for using the Stratistics application.`}
        canonicalPath="/terms/"
      />
      <div className="flex h-svh w-full overflow-hidden bg-background">
        <div className="container mx-auto max-w-3xl px-4 py-10 flex flex-col">
          <div className="mb-6 flex items-center justify-between gap-3">
            <Button variant="ghost" className="-ml-3" onClick={onBack}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              <Trans>Back</Trans>
            </Button>

            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <FileText className="h-4 w-4" />
              <span>
                <Trans>Last updated:</Trans> {lastUpdated}
              </span>
            </div>
          </div>

          <Card className="flex-1 overflow-y-auto">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl">
                <Trans>Terms of Service</Trans>
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                <Trans>
                  These terms govern your use of Stratistics. By using the app,
                  you agree to them.
                </Trans>
              </p>
            </CardHeader>

            <CardContent className="space-y-8">
              <Section title={<Trans>Use of the service</Trans>}>
                <ul className="ml-5 list-disc space-y-1">
                  <li>
                    <Trans>
                      You must use Stratistics in compliance with applicable
                      laws and platform rules (including Strava’s terms).
                    </Trans>
                  </li>
                  <li>
                    <Trans>
                      You are responsible for maintaining the security of your
                      account.
                    </Trans>
                  </li>
                </ul>
              </Section>

              <Section title={<Trans>Strava integration</Trans>}>
                <p>
                  <Trans>
                    Stratistics connects to Strava to read and sync activity
                    data. You can revoke access at any time from your Strava
                    settings.
                  </Trans>
                </p>
              </Section>

              <Section title={<Trans>Content and data</Trans>}>
                <p>
                  <Trans>
                    You retain ownership of your data. You grant Stratistics
                    permission to process your data only to provide the features
                    of the service.
                  </Trans>
                </p>
              </Section>

              <Section title={<Trans>Availability</Trans>}>
                <p>
                  <Trans>
                    The service is provided on an “as is” and “as available”
                    basis. Features may change, be removed, or be interrupted.
                  </Trans>
                </p>
              </Section>

              <Section title={<Trans>Prohibited use</Trans>}>
                <ul className="ml-5 list-disc space-y-1">
                  <li>
                    <Trans>
                      Attempting to gain unauthorized access, interfere with the
                      service, or abuse rate limits.
                    </Trans>
                  </li>
                  <li>
                    <Trans>
                      Using the service to violate the rights or privacy of
                      others.
                    </Trans>
                  </li>
                </ul>
              </Section>

              <Section title={<Trans>Limitation of liability</Trans>}>
                <p>
                  <Trans>
                    To the maximum extent permitted by law, Stratistics is not
                    liable for indirect, incidental, special, consequential, or
                    punitive damages, or any loss of data, profits, or revenues.
                  </Trans>
                </p>
              </Section>

              <Section title={<Trans>Termination</Trans>}>
                <p>
                  <Trans>
                    You may stop using the service at any time. We may suspend
                    or terminate access if necessary to protect the service or
                    enforce these terms.
                  </Trans>
                </p>
              </Section>

              <Section title={<Trans>Changes to these terms</Trans>}>
                <p>
                  <Trans>
                    We may update these terms from time to time. Continued use
                    of the service after changes means you accept the updated
                    terms.
                  </Trans>
                </p>
              </Section>

              <Section title={<Trans>Contact</Trans>}>
                <p>stratistics.app@gmail.com</p>
              </Section>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
